import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Pagination } from '@mui/material';
import useFiltroPersistente from '../../hooks/useFiltroPersistente';
import useBreakpoint from '../../hooks/useBreakpoint';
import BottomSheet from '../mobile/BottomSheet';
import BotonFiltros from '../mobile/BotonFiltros';
import ChipsFiltros from '../mobile/ChipsFiltros';

/**
 * Cuántas filas se dibujan por bloque antes de pedir "Mostrar más". Dibujar las 1.200 carpetas de
 * "A liquidar" de una vez tardaba ~10 s en el navegador (el backend las devuelve en 1 s): lo caro
 * es el DOM, no los datos.
 */
const FILAS_POR_BLOQUE = 50;

/**
 * Las cabeceras de bloque tienen su propio color. `table-hover` de Bootstrap pinta una capa
 * (box-shadow inset) sobre toda celda de la fila bajo el mouse y las dejaba blancas: se anula.
 */
const SIN_HOVER = { boxShadow: 'none' };

/** Una fila de datos. Sale afuera para que agrupado y sin agrupar dibujen exactamente lo mismo. */
function FilaListado({ row, columnas, onRowClick, rowClassName, compacto = false }) {
    return (
        <tr
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            style={{
                cursor: onRowClick ? 'pointer' : 'default',
                transition: 'all 0.2s ease',
                ...(rowClassName ? rowClassName(row) : {}),
            }}
        >
            {columnas.map((col) => (
                <td key={col.key} className={col.className || 'text-center'} style={{ padding: compacto ? '0.5rem 0.45rem' : '1rem' }}>
                    {col.render(row)}
                </td>
            ))}
        </tr>
    );
}

/**
 * Tabla parametrizable para la sección Listados: resuelve búsqueda, paginación, estado de
 * carga/vacío y filas — cada página de listado (ver pages/Listados/definiciones.js) solo declara
 * sus columnas y su fetch. Mismo lenguaje visual (Bootstrap + MUI Pagination) que
 * ListadoTramitesUnificado.js.
 *
 * `columnas`: [{ key, header, render(row), className? }]
 * `fetchPage(page, size, texto)` => Promise<{ content, totalPages }>
 * `agruparPor(row)` => { clave, etiqueta, colapsadoInicial?, subEtiqueta? } | null — opcional. Si
 *   se pasa, las filas se separan en bloques con una fila-cabecera colapsable, como el listado de
 *   trámites ART y como la planilla.
 *   - `colapsadoInicial: true` hace que el bloque nazca cerrado (en "A liquidar", el de meses
 *     anteriores). Se aplica una sola vez por bloque: si el usuario lo abre, queda abierto
 *     aunque el listado se recargue.
 *   - `subEtiqueta` dibuja un separador liviano dentro del bloque cada vez que cambia. Sirve
 *     cuando un bloque junta cosas que igual conviene ver subdivididas — "Meses anteriores"
 *     agrupa varios meses vencidos pero cada uno se sigue leyendo aparte.
 *   - `color` / `colorTexto` pintan la cabecera del bloque (por defecto azul y blanco). En "Para
 *     aprobar" las secciones de novedades van en verde, rojo y amarillo.
 * `compacto` — celdas con menos relleno, para listados con muchas columnas que tienen que entrar en
 *   pantalla sin scroll horizontal ("Para aprobar").
 * `filaDetalle(row)` => nodo | null — opcional. Si devuelve algo, se dibuja en una fila propia
 *   debajo de la fila (el detalle desplegable de "Para aprobar"). El llamador decide cuándo.
 * `chipsExtra`: [{ key, label, onQuitar }] — opcional. Chips de los filtros que declara el
 *   llamador dentro de `filtrosExtra` (esa prop es JSX opaco: TablaListado no puede armar sus
 *   chips solo). Se combinan con el chip de "Buscar" propio en mobile.
 * `reiniciarPaginaCon` — opcional. Cuando su valor cambia, el listado vuelve a la página 1 (el llamador
 *   lo usa con el sentido del orden; el valor mismo también va en `dependencias` para recargar).
 * `onLimpiarExtra` — opcional. Se llama junto con la limpieza del texto propio al tocar
 *   "Limpiar filtros" en la hoja de mobile, para resetear los filtros que son estado del llamador.
 */
export default function TablaListado({
    claveFiltro,
    columnas,
    fetchPage,
    onRowClick,
    rowKey = (row) => row.idCobro ?? row.idTramite,
    rowClassName,
    filtroPlaceholder = 'Buscar por nombre, apellido o CUIL',
    filtrosExtra = null,
    chipsExtra = [],
    onLimpiarExtra = null,
    size = 25,
    dependencias = [],
    reiniciarPaginaCon,
    agruparPor = null,
    filaDetalle = null,
    compacto = false,
}) {
    const [page, setPage] = useFiltroPersistente(`${claveFiltro}.page`, 0);

    // Si el llamador cambia `reiniciarPaginaCon` (por ejemplo, el sentido del orden), se vuelve a la
    // página 1: seguir en la página 5 de un orden que se acaba de dar vuelta no tiene sentido.
    // La primera vez no cuenta: ahí tiene que respetarse la página que se guardó al volver del detalle.
    const primerRenderDelReinicio = useRef(true);
    useEffect(() => {
        if (primerRenderDelReinicio.current) {
            primerRenderDelReinicio.current = false;
            return;
        }
        setPage(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reiniciarPaginaCon]);
    const [texto, setTexto] = useFiltroPersistente(`${claveFiltro}.texto`, '');
    const [filas, setFilas] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(true);
    const [sheetFiltrosAbierto, setSheetFiltrosAbierto] = useState(false);
    const { esMobile } = useBreakpoint();
    // Qué bloques están cerrados. Se guarda al revés (los cerrados, no los abiertos) para que un
    // grupo nuevo nazca abierto sin tener que inicializarlo: el estudio quiere ver todo de una.
    const [gruposCerrados, setGruposCerrados] = useState({});
    // Bloques a los que ya se les aplicó su `colapsadoInicial`. Sin esto, cada recarga del
    // listado volvería a cerrar el bloque que el usuario acaba de abrir.
    const inicialesAplicados = useRef(new Set());
    // Cuántas filas se muestran en cada bloque (ver FILAS_POR_BLOQUE).
    const [limitePorBloque, setLimitePorBloque] = useState({});

    // ── Barra de scroll horizontal ARRIBA de la tabla ───────────────────────
    // Estos listados son anchos (el de Jauregui tiene 10 columnas, y las claves del cliente van
    // casi al final). Con la barra nativa sola, para moverse a la derecha hay que bajar al pie de
    // la página, correr, y volver a subir. Se agrega una barra espejo arriba: es un div vacío del
    // mismo ancho que el contenido de la tabla, y los dos scrolls se sincronizan.
    //
    // Solo aparece cuando la tabla NO entra en el ancho disponible: si entra, una barra de scroll
    // suelta arriba es basura visual.
    const refScrollTabla = useRef(null);
    const refScrollEspejo = useRef(null);
    const [anchoContenido, setAnchoContenido] = useState(0);
    const [hayOverflowHorizontal, setHayOverflowHorizontal] = useState(false);
    // Evita el bucle: al mover uno se mueve el otro, que a su vez dispararía su propio onScroll.
    const sincronizandoScroll = useRef(false);

    useEffect(() => {
        const el = refScrollTabla.current;
        if (!el) return undefined;
        const medir = () => {
            setAnchoContenido(el.scrollWidth);
            // +1 de tolerancia: con zoom o fuentes raras el scrollWidth queda un píxel arriba del
            // clientWidth sin que haya overflow real, y la barra aparecía y desaparecía sola.
            setHayOverflowHorizontal(el.scrollWidth > el.clientWidth + 1);
        };
        medir();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(medir);
        observer.observe(el);
        return () => observer.disconnect();
    }, [filas, columnas, loading]);

    const sincronizarScroll = (origen, destino) => {
        if (!origen || !destino || sincronizandoScroll.current) return;
        sincronizandoScroll.current = true;
        destino.scrollLeft = origen.scrollLeft;
        // Se libera en el frame siguiente: así el onScroll que dispara la línea de arriba ya
        // encuentra el flag puesto y no vuelve a escribir sobre el que el usuario está moviendo.
        requestAnimationFrame(() => { sincronizandoScroll.current = false; });
    };

    /**
     * Las filas partidas en bloques, respetando el orden en que vienen del backend.
     *
     * No reordena nada: el orden lo decide la consulta (en "A liquidar", por fecha estimada de
     * inicio), así que los bloques salen en ese mismo orden. Agrupar acá y no en el backend es a
     * propósito — es una decisión de presentación, y el backend ya pagina.
     */
    const bloques = useMemo(() => {
        if (!agruparPor) return null;
        const orden = [];
        const porClave = new Map();
        filas.forEach((fila) => {
            const g = agruparPor(fila) || { clave: 'sin-grupo', etiqueta: 'Sin agrupar' };
            if (!porClave.has(g.clave)) {
                porClave.set(g.clave, { ...g, filas: [] });
                orden.push(g.clave);
            }
            porClave.get(g.clave).filas.push(fila);
        });
        return orden.map((c) => porClave.get(c));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filas, agruparPor]);

    const alternarGrupo = (clave) =>
        setGruposCerrados((prev) => ({ ...prev, [clave]: !prev[clave] }));

    // Si el usuario no tocó un bloque, manda `colapsadoInicial`.
    const estaCerrado = (bloque) => (bloque.clave in gruposCerrados
        ? Boolean(gruposCerrados[bloque.clave])
        : Boolean(bloque.colapsadoInicial));
    const todosPlegados = Boolean(bloques) && bloques.every(estaCerrado);

    const plegarTodos = (cerrar) => {
        if (!bloques) return;
        setGruposCerrados(Object.fromEntries(bloques.map((b) => [b.clave, cerrar])));
    };

    // Cierra de entrada los bloques que lo piden, una sola vez cada uno (ver `inicialesAplicados`).
    useEffect(() => {
        if (!bloques) return;
        const aCerrar = bloques.filter((b) => b.colapsadoInicial && !inicialesAplicados.current.has(b.clave));
        if (aCerrar.length === 0) return;
        aCerrar.forEach((b) => inicialesAplicados.current.add(b.clave));
        setGruposCerrados((prev) => {
            const siguiente = { ...prev };
            aCerrar.forEach((b) => { siguiente[b.clave] = true; });
            return siguiente;
        });
    }, [bloques]);

    useEffect(() => {
        let cancelado = false;
        setLoading(true);
        fetchPage(page, size, texto)
            .then((data) => {
                if (cancelado) return;
                setFilas(data?.content || []);
                setTotalPages(data?.totalPages || 0);
            })
            .catch((err) => {
                console.error('Error al cargar el listado:', err);
                if (!cancelado) {
                    setFilas([]);
                    setTotalPages(0);
                }
            })
            .finally(() => { if (!cancelado) setLoading(false); });
        return () => { cancelado = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, texto, ...dependencias]);

    const campoBuscar = (
        <div style={{ flex: '1 1 280px' }}>
            <label className="form-label" >
                <i className="bi bi-search me-2"></i>Buscar
            </label>
            <input
                type="text"
                className="form-control"
                placeholder={filtroPlaceholder}
                value={texto}
                onChange={(e) => { setPage(0); setTexto(e.target.value); }}
            />
        </div>
    );

    const chipsFiltros = [
        texto && { key: 'texto', label: texto, onQuitar: () => { setPage(0); setTexto(''); } },
        ...chipsExtra,
    ].filter(Boolean);

    const limpiarTodo = () => {
        setPage(0);
        setTexto('');
        onLimpiarExtra?.();
    };

    return (
        <div>
            {esMobile ? (
                <div className="mb-3">
                    <BotonFiltros cantidad={chipsFiltros.length} onClick={() => setSheetFiltrosAbierto(true)} />
                    <ChipsFiltros chips={chipsFiltros} />
                    {sheetFiltrosAbierto && (
                        <BottomSheet
                            titulo="Filtros"
                            onCerrar={() => setSheetFiltrosAbierto(false)}
                            accionPrimaria={chipsFiltros.length > 0 ? { label: 'Limpiar filtros', onClick: limpiarTodo } : undefined}
                        >
                            <div className="mb-3">{campoBuscar}</div>
                            <div className="mob-campos-extra">{filtrosExtra}</div>
                        </BottomSheet>
                    )}
                </div>
            ) : (
                <div className="card shadow-sm mb-3" style={{ border: 'none', borderRadius: '12px' }}>
                    <div className="card-body d-flex flex-wrap align-items-end gap-3" style={{ padding: '1.25rem' }}>
                        {campoBuscar}
                        {filtrosExtra}
                    </div>
                </div>
            )}

            {loading ? (
                <div className="text-center my-5">
                    <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }}></div>
                    <p className="mt-3 fw-semibold" style={{ color: '#6B7280' }}>Cargando...</p>
                </div>
            ) : (
                <div className="card shadow-sm" style={{ border: 'none', borderRadius: '12px' }}>
                    {bloques && bloques.length > 1 && (
                        <div className="d-flex justify-content-end px-3 pt-2 pb-2">
                            {/* Un solo botón: dice lo que va a hacer. Si están todos plegados
                                despliega; si hay alguno abierto, pliega. */}
                            <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => plegarTodos(!todosPlegados)}>
                                <i className={`bi ${todosPlegados ? 'bi-arrows-expand' : 'bi-arrows-collapse'} me-1`} />
                                {todosPlegados ? 'Desplegar todos' : 'Plegar todos'}
                            </button>
                        </div>
                    )}
                    {hayOverflowHorizontal && (
                        <div
                            ref={refScrollEspejo}
                            onScroll={() => sincronizarScroll(refScrollEspejo.current, refScrollTabla.current)}
                            style={{
                                overflowX: 'auto', overflowY: 'hidden',
                                borderTopLeftRadius: '12px', borderTopRightRadius: '12px',
                            }}
                            aria-hidden="true"
                        >
                            {/* Div vacío del ancho del contenido: es lo único que le da al
                                navegador algo que scrollear. 1px de alto para que no ocupe
                                espacio más allá de la propia barra. */}
                            <div style={{ width: anchoContenido, height: 1 }} />
                        </div>
                    )}
                    <div
                        className="table-responsive"
                        ref={refScrollTabla}
                        onScroll={() => sincronizarScroll(refScrollTabla.current, refScrollEspejo.current)}
                    >
                        <table className="table table-hover align-middle mb-0">
                            <thead style={{ background: 'var(--gradient-primary-light)', borderBottom: '2px solid #3B82F6' }}>
                                <tr>
                                    {columnas.map((col) => (
                                        <th key={col.key} className="text-center" style={{ padding: compacto ? '0.6rem 0.45rem' : '1rem', fontWeight: 600, color: '#1e40af', fontSize: compacto ? '0.85rem' : undefined }}>
                                            {col.header}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {bloques
                                    ? bloques.map((bloque) => {
                                        // Sin decisión del usuario manda `colapsadoInicial`. Esperar al
                                        // efecto de arriba hacía que el primer dibujo saliera con todos
                                        // los bloques abiertos (~1.000 filas en A liquidar) para plegarlos
                                        // recién en el segundo: eran 2-3 s perdidos.
                                        const cerrado = estaCerrado(bloque);
                                        const limite = limitePorBloque[bloque.clave] || FILAS_POR_BLOQUE;
                                        const restantes = bloque.filas.length - limite;
                                        return (
                                            <Fragment key={bloque.clave}>
                                                <tr>
                                                    <td
                                                        colSpan={columnas.length}
                                                        onClick={() => alternarGrupo(bloque.clave)}
                                                        style={{
                                                            ...SIN_HOVER,
                                                            background: bloque.color || '#1e40af', color: bloque.colorTexto || '#fff', cursor: 'pointer',
                                                            padding: '0.7rem 1rem', fontWeight: 700, userSelect: 'none',
                                                        }}
                                                    >
                                                        <span className="d-flex align-items-center gap-3">
                                                            <i className={`bi ${cerrado ? 'bi-chevron-right' : 'bi-chevron-down'}`} />
                                                            <span>{bloque.etiqueta}</span>
                                                            <span style={{
                                                                background: 'rgba(255,255,255,0.25)', borderRadius: 20,
                                                                padding: '2px 12px', fontSize: '0.82rem', fontWeight: 600,
                                                            }}>
                                                                {bloque.filas.length}
                                                            </span>
                                                        </span>
                                                    </td>
                                                </tr>
                                                {!cerrado && bloque.filas.slice(0, limite).map((row, i) => {
                                                    // El separador se dibuja cuando la subEtiqueta cambia respecto
                                                    // de la fila anterior: las filas ya vienen ordenadas, así que
                                                    // eso alcanza para partir el bloque sin re-agrupar nada.
                                                    const sub = agruparPor(row)?.subEtiqueta;
                                                    const subPrevia = i === 0 ? null : agruparPor(bloque.filas[i - 1])?.subEtiqueta;
                                                    return (
                                                        <Fragment key={rowKey(row)}>
                                                            {sub && sub !== subPrevia && (
                                                                <tr>
                                                                    <td colSpan={columnas.length} style={{
                                                                        ...SIN_HOVER,
                                                                        background: '#eef2ff', color: '#3730a3',
                                                                        padding: '0.35rem 1rem', fontWeight: 600,
                                                                        fontSize: '0.82rem', letterSpacing: '0.02em',
                                                                    }}>
                                                                        {sub}
                                                                    </td>
                                                                </tr>
                                                            )}
                                                            <FilaListado
                                                                row={row} columnas={columnas}
                                                                onRowClick={onRowClick} rowClassName={rowClassName} compacto={compacto}
                                                            />
                                                            <FilaDetalle row={row} columnas={columnas} filaDetalle={filaDetalle} />
                                                        </Fragment>
                                                    );
                                                })}
                                                {!cerrado && restantes > 0 && (
                                                    <tr>
                                                        <td colSpan={columnas.length} className="text-center" style={{ ...SIN_HOVER, background: '#f8fafc' }}>
                                                            <button
                                                                type="button" className="btn btn-sm btn-outline-primary"
                                                                onClick={() => setLimitePorBloque((prev) => ({ ...prev, [bloque.clave]: limite + FILAS_POR_BLOQUE }))}
                                                            >
                                                                Mostrar {Math.min(restantes, FILAS_POR_BLOQUE)} más ({restantes} sin mostrar)
                                                            </button>
                                                        </td>
                                                    </tr>
                                                )}
                                            </Fragment>
                                        );
                                    })
                                    : filas.map((row) => (
                                        <Fragment key={rowKey(row)}>
                                            <FilaListado
                                                row={row} columnas={columnas}
                                                onRowClick={onRowClick} rowClassName={rowClassName} compacto={compacto}
                                            />
                                            <FilaDetalle row={row} columnas={columnas} filaDetalle={filaDetalle} />
                                        </Fragment>
                                    ))}
                            </tbody>
                        </table>
                        {filas.length === 0 && (
                            <div className="text-center py-5">
                                <i className="bi bi-inbox" style={{ fontSize: '3rem', color: '#6b7280' }}></i>
                                <p className="mt-3" style={{ color: '#64748b', fontSize: '1.1rem' }}>No hay resultados.</p>
                            </div>
                        )}
                        {totalPages > 0 && (
                            <div className="d-flex justify-content-center py-3 border-top">
                                <Pagination
                                    count={totalPages}
                                    page={page + 1}
                                    onChange={(_e, value) => setPage(value - 1)}
                                    color="primary"
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

/** La fila desplegable debajo de una fila, si el llamador pasó `filaDetalle` y devuelve algo. */
function FilaDetalle({ row, columnas, filaDetalle }) {
    const contenido = filaDetalle ? filaDetalle(row) : null;
    if (!contenido) return null;
    return (
        <tr>
            <td colSpan={columnas.length} style={{ ...SIN_HOVER, background: '#f8fafc', padding: '0.75rem 1.25rem' }}>
                {contenido}
            </td>
        </tr>
    );
}
